import { useState, useEffect } from 'react';
import { Meeting, Recording } from '../types/meeting';
import { sampleMeetings, sampleRecordings } from '../data/sampleMeetings';

export function useZoomMeetings() {
  const [meetings, setMeetings] = useState<Meeting[]>(sampleMeetings);
  const [recordings, setRecordings] = useState<Recording[]>(sampleRecordings);
  const [loading, setLoading] = useState(false);

  const startMeeting = async (meeting: Omit<Meeting, 'id' | 'startTime' | 'participants'>) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMeeting: Meeting = {
        ...meeting,
        id: crypto.randomUUID(),
        startTime: new Date().toISOString(),
        participants: 1
      };
      
      setMeetings([newMeeting, ...meetings]);
      
      // In a real app, this would return a Zoom meeting URL
      window.open('https://zoom.us/start/videomeeting', '_blank');
    } finally {
      setLoading(false);
    }
  };

  const scheduleMeeting = async (meeting: Omit<Meeting, 'id'>) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMeeting: Meeting = {
        ...meeting,
        id: crypto.randomUUID()
      };
      
      setMeetings([...meetings, newMeeting].sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      ));
      
      return newMeeting;
    } finally {
      setLoading(false);
    }
  };

  const joinMeeting = async (meetingId: string) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        setMeetings(meetings.map(m => 
          m.id === meetingId 
            ? { ...m, participants: m.participants + 1 }
            : m
        ));
      }
      
      // In a real app, this would return a Zoom meeting URL
      window.open('https://zoom.us/join', '_blank');
    } finally {
      setLoading(false);
    }
  };

  const endMeeting = async (meetingId: string) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        const recording: Recording = {
          id: crypto.randomUUID(),
          topic: meeting.topic,
          recordingDate: new Date().toISOString(),
          duration: meeting.duration,
          fileSize: Math.floor(Math.random() * 500000000) + 50000000 // Random size between 50MB and 550MB
        };
        
        setRecordings([recording, ...recordings]);
        setMeetings(meetings.filter(m => m.id !== meetingId));
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadRecording = async (recordingId: string) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const recording = recordings.find(r => r.id === recordingId);
      if (recording) {
        // In a real app, this would trigger the actual download
        console.log(`Downloading recording: ${recording.topic}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    meetings,
    recordings,
    loading,
    startMeeting,
    scheduleMeeting,
    joinMeeting,
    endMeeting,
    downloadRecording
  };
}